import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  mapCategoryError,
  useAuthSession,
  useCreateCategory,
  useDeleteCategory,
  useReorderCategories,
  useStoreCategories,
  useTranslation,
  useUpdateCategory,
} from '@dukanti/shared';
import type { CategoryErrorKey, StoreCategoryWithMedia, TranslationKey } from '@dukanti/shared';
import { AlertCircle, Plus } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useCurrentStore } from '@/hooks/use-current-store';
import { Button } from '@/components/ui/button';
import { CategoryFormDialog } from '@/features/categories/components/category-form-dialog';
import type { CategoryFormSubmit } from '@/features/categories/components/category-form-dialog';
import { CategoryList } from '@/features/categories/components/category-list';
import { CategoriesEmptyState } from '@/features/categories/components/categories-empty-state';
import { CategoriesListSkeleton } from '@/features/categories/components/categories-list-skeleton';
import { DeleteCategoryDialog } from '@/features/categories/components/delete-category-dialog';

export function CategoriesRoute() {
  const { t, dir } = useTranslation();
  const authSession = useAuthSession(supabase);
  const { storeId } = useCurrentStore(authSession);
  const categoriesQuery = useStoreCategories(supabase, storeId);
  const createCategory = useCreateCategory(supabase);
  const updateCategory = useUpdateCategory(supabase);
  const reorderCategories = useReorderCategories(supabase);
  const deleteCategory = useDeleteCategory(supabase);

  const [editingCategory, setEditingCategory] = useState<StoreCategoryWithMedia | null>(null);
  const [deletingCategory, setDeletingCategory] = useState<StoreCategoryWithMedia | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formError, setFormError] = useState<CategoryErrorKey | null>(null);
  const [deleteError, setDeleteError] = useState<CategoryErrorKey | null>(null);

  const categories = useMemo(
    () => [...categoriesQuery.categories].sort((a, b) => a.display_order - b.display_order),
    [categoriesQuery.categories],
  );

  function openCreate(): void {
    setEditingCategory(null);
    setFormError(null);
    setIsFormOpen(true);
  }

  function openEdit(category: StoreCategoryWithMedia): void {
    setEditingCategory(category);
    setFormError(null);
    setIsFormOpen(true);
  }

  async function handleFormSubmit(values: CategoryFormSubmit): Promise<void> {
    if (!storeId) {
      setFormError('admin.categories.errors.permissionDenied');
      return;
    }

    try {
      if (editingCategory) {
        await updateCategory.mutateAsync({
          category_id: editingCategory.id,
          name: values.name,
          public_description: values.publicDescription,
          image: values.image,
          removeImage: values.removeImage,
        });
        toast.success(t('admin.categories.toasts.updated'));
      } else {
        await createCategory.mutateAsync({
          store_id: storeId,
          name: values.name,
          public_description: values.publicDescription,
          image: values.image,
        });
        toast.success(t('admin.categories.toasts.created'));
      }
      setIsFormOpen(false);
      setEditingCategory(null);
    } catch (err) {
      const mapped = mapCategoryError(err);
      setFormError(mapped);
      toast.error(t(mapped as TranslationKey));
    }
  }

  async function handleMove(category: StoreCategoryWithMedia, direction: 'up' | 'down') {
    if (!storeId) return;
    const currentIndex = categories.findIndex((item) => item.id === category.id);
    const nextIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (currentIndex < 0 || nextIndex < 0 || nextIndex >= categories.length) return;

    const next = [...categories];
    const current = next[currentIndex];
    const target = next[nextIndex];
    if (!current || !target) return;
    next[currentIndex] = target;
    next[nextIndex] = current;

    try {
      await reorderCategories.mutateAsync({
        store_id: storeId,
        ordered_category_ids: next.map((item) => item.id),
      });
      toast.success(t('admin.categories.toasts.reordered'));
    } catch (err) {
      const mapped = mapCategoryError(err);
      toast.error(t(mapped as TranslationKey));
    }
  }

  function openDelete(category: StoreCategoryWithMedia): void {
    setDeletingCategory(category);
    setDeleteError(null);
  }

  function handleDeleteOpenChange(open: boolean): void {
    if (!open) {
      setDeletingCategory(null);
      setDeleteError(null);
    }
  }

  function handleDeleteConfirm(): void {
    if (!deletingCategory) return;
    deleteCategory.mutate(
      { category_id: deletingCategory.id },
      {
        onSuccess: () => {
          toast.success(t('admin.categories.toasts.deleted'));
          setDeletingCategory(null);
          setDeleteError(null);
        },
        onError: (err) => {
          const mapped = mapCategoryError(err);
          setDeleteError(mapped);
          toast.error(t(mapped as TranslationKey));
        },
      },
    );
  }

  const isLoading = categoriesQuery.isLoading;
  const isError = categoriesQuery.isError || !storeId;
  const errorKey = !storeId ? 'common.errors.permissionDenied' : categoriesQuery.errorKey;

  return (
    <main
      dir={dir}
      data-testid="categories-route"
      className="mx-auto w-full max-w-5xl px-4 py-6 sm:px-6 lg:px-8"
    >
      <div className="mb-6 flex flex-col justify-between gap-4 sm:flex-row sm:items-start">
        <div>
          <h1 className="font-display text-foreground text-start text-[28px] font-bold leading-tight tracking-tight">
            {t('admin.categories.title')}
          </h1>
          <p className="text-muted-foreground mt-1 max-w-2xl text-start text-sm">
            {t('admin.categories.subtitle')}
          </p>
        </div>
        <Button type="button" onClick={openCreate} disabled={!storeId}>
          <Plus className="size-4" aria-hidden />
          <span>{t('admin.categories.addNew')}</span>
        </Button>
      </div>

      {isLoading ? <CategoriesListSkeleton /> : null}

      {!isLoading && isError ? (
        <div
          role="alert"
          data-testid="categories-route-error"
          className="border-border bg-card shadow-admin-card mx-auto max-w-lg rounded-lg border p-8 text-center"
        >
          <AlertCircle className="text-destructive mx-auto mb-4 size-10" strokeWidth={1.5} />
          <h2 className="font-display text-foreground mb-2 text-xl">{t('common.error')}</h2>
          <p className="text-muted-foreground mb-5 text-sm">
            {t((errorKey ?? 'common.errors.unknown') as TranslationKey)}
          </p>
          {storeId ? (
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                void categoriesQuery.refetch();
              }}
            >
              {t('common.retry')}
            </Button>
          ) : null}
        </div>
      ) : null}

      {!isLoading && !isError && categories.length === 0 ? (
        <CategoriesEmptyState onCreate={openCreate} />
      ) : null}

      {!isLoading && !isError && categories.length > 0 ? (
        <CategoryList
          categories={categories}
          isReordering={reorderCategories.isPending}
          onEdit={openEdit}
          onDelete={openDelete}
          onMove={handleMove}
        />
      ) : null}

      <CategoryFormDialog
        open={isFormOpen}
        category={editingCategory}
        isSubmitting={createCategory.isPending || updateCategory.isPending}
        errorKey={formError}
        onOpenChange={setIsFormOpen}
        onSubmit={handleFormSubmit}
      />
      <DeleteCategoryDialog
        category={deletingCategory}
        open={deletingCategory !== null}
        isDeleting={deleteCategory.isPending}
        errorKey={deleteError}
        onOpenChange={handleDeleteOpenChange}
        onConfirm={handleDeleteConfirm}
      />
    </main>
  );
}
